import React from "react";
import { config } from "~/config.js";
import { Level } from "../badges/Level.jsx";
import { calculateLevel } from "../badges/LevelProgress.jsx";

// Images smaller than this threshold use the thumbnail URL.
const THUMB_THRESHOLD = 120;

export const getUserAvatar = (user, isThumb) => {
  if (user && user.uuid) {
    // c= is a cache-bust to refresh avatars after an upload
    return (
      config.avatar_prefix +
        user.uuid +
        (isThumb ? "/avatar_thumb.jpg" : "/avatar.jpg") +
        "?c=" +
        user.date_updated || -1
    );
  }
  return window.location.origin + "/avatar.png";
};

export const getUserAvatarError = (element) => {
  if (navigator.onLine) {
    element.target.src = window.location.origin + "/avatar.png";
  }
};

/**
 * Renders a user avatar <img>.
 *
 * Props:
 *   user      {object}  Object with `uuid` and optionally `name`, `date_updated`.
 *   size      {number}  Logical size in px (default 32). Values < 120 load the
 *                       thumbnail URL; ≥ 120 load the full-size URL.
 *   className {string}  CSS class(es) — e.g. "avatarLedenLijst" for 40×40 rounded.
 *   style     {object}  Inline styles — pass explicit width/height when needed.
 *   onClick   {func}
 *   alt       {string}  Defaults to user?.name.
 *   showBadgeLevel {boolean} Whether to show the user's badge level as a small overlay.
 */
export const UserAvatar = ({
  user,
  size = 32,
  className = "",
  style = {},
  onClick = null,
  alt,
  showBadgeLevel = false,
}) => {
  const badgeLevel =
    showBadgeLevel && (user?.badge_level || user?.recency?.badge_level || null);
  const img = (
    <img
      alt={alt ?? user?.name ?? ""}
      className={className}
      style={{
        borderRadius: "50%",
        height: size,
        width: size,
        display: "block",
        ...style,
      }}
      src={getUserAvatar(user, size < THUMB_THRESHOLD)}
      onError={getUserAvatarError}
      onClick={onClick}
    />
  );

  if (showBadgeLevel && badgeLevel > 0) {
    return (
      <Level level={calculateLevel(badgeLevel)} size="small">
        {img}
      </Level>
    );
  }

  return img;
};
